import { createContext, useCallback, useContext, useEffect, useId, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { discardChanges, useConfirm } from "../components/ConfirmProvider";

const NavigationContext = createContext(null);
export function NavigationGuard({ children }) {
  const forms = useRef(new Map());
  const confirm = useConfirm();
  const context = useRef({
    update: (id, dirty) => forms.current.set(id, dirty),
    remove: (id) => forms.current.delete(id),
    release: () => forms.current.clear(),
    prepareLeave: async () => {
      if ([...forms.current.values()].some(Boolean) && !(await confirm(discardChanges))) return false;
      return true;
    },
  });
  const blocker = useBlocker(useCallback(() => [...forms.current.values()].some(Boolean), []));
  const asking = useRef(false);
  const latestBlocker = useRef(blocker);
  latestBlocker.current = blocker;
  useEffect(() => {
    if (blocker.state !== "blocked" || asking.current) return;
    asking.current = true;
    confirm(discardChanges).then((accepted) => {
      const current = latestBlocker.current;
      if (current.state === "blocked") {
        if (accepted) current.proceed(); else current.reset();
      }
      asking.current = false;
    });
  }, [blocker, confirm]);
  return <NavigationContext.Provider value={context.current}>{children}</NavigationContext.Provider>;
}

export default function useUnsavedChanges(dirty) {
  const context = useContext(NavigationContext);
  const id = useId();
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => { context?.update(id, dirty); }, [context, id, dirty]);
  useEffect(() => () => context?.remove(id), [context, id]);
  useEffect(() => {
    const preventLoss = (event) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventLoss);
    return () => window.removeEventListener("beforeunload", preventLoss);
  }, []);
  // Mettre à jour le registre synchroniquement avant la navigation qui suit un enregistrement réussi.
  return useCallback(() => { dirtyRef.current = false; context?.update(id, false); }, [context, id]);
}

export function useLeaveConfirmation() {
  const context = useContext(NavigationContext);
  return context?.prepareLeave || (async () => true);
}

export function useReleaseNavigation() {
  const context = useContext(NavigationContext);
  return context?.release || (() => {});
}
